/**
 * 修改密码卡片(Profile 内嵌)
 *
 * 流程:
 *   - 已设密码:输入旧密码 + 新密码 → 改
 *   - 未设密码:跳转登录弹窗"设置密码"流程
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, ShieldCheck, X, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../lib/api.js';

export default function ChangePasswordCard({ user }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('verify');  // 'verify' | 'set' | 'change'
  const [verifyCode, setVerifyCode] = useState('');
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const hasPassword = user?.has_password;

  const handleSetPassword = async () => {
    if (newPwd.length < 6) {
      toast.error('密码至少 6 位');
      return;
    }
    if (newPwd.length > 64) {
      toast.error('密码不能超过 64 位');
      return;
    }
    if (/^\d+$/.test(newPwd)) {
      toast.error('密码不能全是数字');
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error('两次密码不一致');
      return;
    }
    if (verifyCode.length !== 6) {
      toast.error('请输入 6 位验证码');
      return;
    }
    setLoading(true);
    try {
      const res = await api.setPassword(newPwd, verifyCode);
      if (res.ok) {
        toast.success('密码已设置!');
        setOpen(false);
        // 更新 user 状态
        if (user) user.has_password = true;
      } else {
        toast.error(res.error || '设置失败');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  const handleChangePassword = async () => {
    if (oldPwd.length < 6) {
      toast.error('请输入原密码');
      return;
    }
    if (newPwd.length < 6) {
      toast.error('新密码至少 6 位');
      return;
    }
    if (newPwd === oldPwd) {
      toast.error('新密码不能跟旧密码一样');
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error('两次密码不一致');
      return;
    }
    setLoading(true);
    try {
      const res = await api.changePassword(oldPwd, newPwd);
      if (res.ok) {
        toast.success('密码已修改!');
        setOpen(false);
      } else {
        toast.error(res.error || '修改失败');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  const handleSendCode = async () => {
    try {
      const res = await api.sendCode(user.phone);
      if (res.ok) {
        toast.success('验证码已发送');
        if (res._devCode) {
          toast.success(`开发模式验证码:${res._devCode}`, { duration: 5000 });
        }
      } else {
        toast.error('发送失败');
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!user) return null;

  return (
    <>
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-purple to-brand-cyan flex items-center justify-center">
              <ShieldCheck size={20} className="text-white" />
            </div>
            <div>
              <div className="text-sm font-bold">账户安全</div>
              <div className="text-xs text-ink-2">
                {hasPassword ? (
                  <span className="text-brand-cyan">✓ 已设置密码</span>
                ) : (
                  <span className="text-amber-400">⚠ 未设置密码(只能用验证码登录)</span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setOpen(true);
              setStep(hasPassword ? 'change' : 'set');
              setVerifyCode('');
              setOldPwd('');
              setNewPwd('');
              setConfirmPwd('');
            }}
            className="btn-secondary text-xs"
          >
            {hasPassword ? '修改密码' : '设置密码'}
          </button>
        </div>
      </div>

      {/* 修改/设置密码弹窗 */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="w-full sm:max-w-md glass-strong rounded-2xl p-6 relative"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 text-ink-3 hover:text-ink-0"
              >
                <X size={20} />
              </button>

              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl gradient-bg mb-2">
                  <Lock size={22} className="text-bg-base" />
                </div>
                <h2 className="text-lg font-bold">
                  {step === 'change' ? '🔐 修改密码' : '🔐 设置密码'}
                </h2>
                <p className="text-xs text-ink-3 mt-1">
                  {step === 'change'
                    ? '修改后请用新密码登录'
                    : '设密码后可用密码登录,更便捷'}
                </p>
              </div>

              <div className="space-y-3">
                {/* 已设密码 → 需要旧密码 */}
                {step === 'change' && (
                  <PwdInput
                    label="原密码"
                    value={oldPwd}
                    onChange={setOldPwd}
                    show={showPwd}
                    onToggleShow={() => setShowPwd(!showPwd)}
                    placeholder="输入原密码"
                  />
                )}

                {/* 未设密码 → 需要验证码二次验证 */}
                {step === 'set' && (
                  <div>
                    <label className="text-xs text-ink-3 mb-1.5 block">手机验证码</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={verifyCode}
                        onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="输入 6 位"
                        className="flex-1 bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 text-center text-lg font-mono tracking-[0.3em] outline-none focus:border-brand-purple/40"
                      />
                      <button
                        onClick={handleSendCode}
                        className="btn-secondary text-xs px-3 whitespace-nowrap"
                      >
                        发送验证码
                      </button>
                    </div>
                    <div className="text-[10px] text-ink-3 mt-1">开发模式验证码:123456</div>
                  </div>
                )}

                <PwdInput
                  label="新密码(6-64 位,不能全是数字)"
                  value={newPwd}
                  onChange={setNewPwd}
                  show={showPwd}
                  onToggleShow={() => setShowPwd(!showPwd)}
                  placeholder="建议字母+数字组合"
                />

                <PwdInput
                  label="确认新密码"
                  value={confirmPwd}
                  onChange={setConfirmPwd}
                  show={showPwd}
                  onToggleShow={() => setShowPwd(!showPwd)}
                  placeholder="再输一次"
                />

                {/* 强度提示 */}
                {newPwd && (
                  <PwdStrength value={newPwd} />
                )}

                <div className="grid grid-cols-2 gap-2 pt-2">
                  <button
                    onClick={() => setOpen(false)}
                    className="btn-secondary"
                  >
                    取消
                  </button>
                  <button
                    onClick={step === 'change' ? handleChangePassword : handleSetPassword}
                    disabled={loading}
                    className={clsx(
                      'py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2',
                      !loading ? 'btn-primary' : 'bg-white/5 text-ink-3 cursor-not-allowed'
                    )}
                  >
                    <Check size={14} />
                    {step === 'change' ? '确认修改' : '确认设置'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function PwdInput({ label, value, onChange, show, onToggleShow, placeholder }) {
  return (
    <div>
      <label className="text-xs text-ink-3 mb-1.5 block">{label}</label>
      <div className="flex items-center gap-2 bg-bg-card border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-brand-purple/40 transition">
        <Lock size={14} className="text-ink-3" />
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent outline-none text-sm"
        />
        <button onClick={onToggleShow} className="text-ink-3">
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}

function PwdStrength({ value }) {
  let level = 0;
  if (value.length >= 8) level++;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) level++;
  if (/\d/.test(value)) level++;
  if (/[^A-Za-z0-9]/.test(value)) level++;

  const labels = ['弱', '中弱', '中', '中强', '强'];
  const colors = ['text-accent-danger', 'text-orange-400', 'text-amber-400', 'text-brand-cyan', 'text-brand-gold'];
  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="flex-1 grid grid-cols-4 gap-1">
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className={clsx(
              'h-1 rounded-full transition',
              i < level ? 'bg-current' : 'bg-white/10',
              colors[level - 1] || 'text-white/20'
            )}
          />
        ))}
      </div>
      <span className={clsx('font-semibold', colors[level - 1] || 'text-ink-3')}>
        {labels[level - 1] || '太弱'}
      </span>
    </div>
  );
}
